import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, UserPlus, Trash2, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

const err = (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar");

const StoresPick = ({ stores, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {stores.map((s) => {
      const on = value.includes(s.store_id);
      return (
        <label key={s.store_id} className={`flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border cursor-pointer ${on ? "border-sky-300 bg-sky-50" : "border-slate-200"}`}>
          <Checkbox data-testid={`${testPrefix}-${s.store_id}`} checked={on} onCheckedChange={(v) => onChange(v ? [...value, s.store_id] : value.filter((x) => x !== s.store_id))} />
          {s.nombre} <span className="font-mono text-slate-400">{s.usados}/{s.limite}</span>
        </label>
      );
    })}
  </div>
);

const Nuevo = ({ d, onDone }) => {
  const [uid, setUid] = useState("");
  const [sids, setSids] = useState([]);
  const grant = async () => {
    try { await api.post("/mobile/access", { user_id: uid, store_ids: sids }); toast.success("Acceso habilitado"); setUid(""); setSids([]); onDone(); } catch (e) { err(e); }
  };
  if (!d.candidatos.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="mobile-users-new">
      <p className="font-semibold text-sm flex items-center gap-2"><UserPlus className="w-4 h-4 text-sky-600" />Habilitar un usuario</p>
      <select data-testid="mobile-users-new-user" value={uid} onChange={(e) => setUid(e.target.value)} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm">
        <option value="">Elige un usuario…</option>
        {d.candidatos.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email} ({u.role})</option>)}
      </select>
      <StoresPick stores={d.stores} value={sids} onChange={setSids} testPrefix="mobile-users-new-store" />
      <Button data-testid="mobile-users-new-submit" disabled={!uid || !sids.length} onClick={grant} className="bg-sky-600 hover:bg-sky-700">Habilitar acceso</Button>
    </div>
  );
};

export default function MobileUsers() {
  const [d, setD] = useState(null);
  const load = () => api.get("/mobile/access").then((r) => setD(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  if (!d) return <p className="p-6 text-slate-500">Cargando…</p>;
  const upd = async (uid, body) => { try { await api.put(`/mobile/access/${uid}`, body); load(); } catch (e) { err(e); } };
  const del = async (uid) => { if (!window.confirm("¿Quitar el acceso a la app? Se cerrarán sus notificaciones.")) return; await api.delete(`/mobile/access/${uid}`); load(); };
  const url = `${window.location.origin}/movil`;
  return (
    <div className="space-y-5 max-w-4xl" data-testid="mobile-users-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display flex items-center gap-2"><Smartphone className="w-6 h-6 text-sky-600" />Usuarios App Móvil</h1>
          <p className="text-sm text-slate-500 mt-1">Plan <b>{d.tier === "PRO" ? "Pro" : "Start"}</b>: hasta <b data-testid="mobile-users-limit">{d.limite}</b> usuario(s) de la app por sucursal.</p>
        </div>
        <a href={url} target="_blank" rel="noreferrer" data-testid="mobile-users-open-app" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 border border-sky-200 rounded-xl px-3 py-2 hover:bg-sky-50">
          <QrCode className="w-4 h-4" /> Abrir app: {url.replace(/^https?:\/\//, "")}
        </a>
      </div>
      <div className="grid sm:grid-cols-3 gap-2" data-testid="mobile-users-cupos">
        {d.stores.map((s) => (
          <div key={s.store_id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`mobile-users-cupo-${s.store_id}`}>
            <p className="text-xs text-slate-500 truncate">{s.nombre}</p>
            <p className={`font-mono font-bold ${s.usados >= s.limite ? "text-amber-600" : "text-slate-900"}`}>{s.usados} / {s.limite}</p>
          </div>
        ))}
      </div>
      <Nuevo d={d} onDone={load} />
      <div className="space-y-2">
        {d.accesos.map((a) => (
          <div key={a.user_id} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`mobile-users-row-${a.user_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{a.name} <Badge variant="outline" className="ml-1">{a.role}</Badge></p>
                <p className="text-xs text-slate-500">{a.email} · {a.dispositivos} dispositivo(s) con notificaciones · {a.last_seen ? `último uso ${new Date(a.last_seen).toLocaleString("es-CL")}` : "aún no entra"}</p>
              </div>
              <label className="flex items-center gap-2 text-xs">Activo <Switch data-testid={`mobile-users-active-${a.user_id}`} checked={!!a.activo} onCheckedChange={(v) => upd(a.user_id, { activo: v })} /></label>
              <Button size="sm" variant="ghost" data-testid={`mobile-users-delete-${a.user_id}`} onClick={() => del(a.user_id)} className="text-rose-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <StoresPick stores={d.stores} value={a.store_ids || []} onChange={(v) => upd(a.user_id, { store_ids: v })} testPrefix={`mobile-users-store-${a.user_id}`} />
          </div>
        ))}
        {!d.accesos.length && <p className="text-sm text-slate-500">Nadie tiene acceso aún. El dueño queda habilitado automáticamente al abrir la app.</p>}
      </div>
    </div>
  );
}
